import React, { useEffect } from 'react';
import { Divider, Form, message } from 'antd';
import axios from 'axios';
import './auth.scss';
import img1 from '../../assets/images/Bloggr.ai.png';
import logo from '../../assets/icons/Bloggr.ai.svg';

import googleImg from '../../assets/images/flat-color-icons_google.png';
import microsofrtImg from '../../assets/images/microsoft.png';
import { useNavigate } from 'react-router-dom';
import blogImg from '../../assets/images/write-blog.png';
import { setAuth } from '../../utils/auth';

function Login() {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const onFinish = async (values: any) => {
    try {
      const result = await axios.post(`${process.env.REACT_APP_SERVER_URL}/login`, values);
      if (result.status !== 200) {
        message.error(result?.data?.error);
      } else {
        message.success(result?.data?.message);
        setAuth(result.data.token, result.data.userId);
        form.resetFields();
        navigate('/');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.error ? error?.response?.data?.error : error?.message);
    }
  };

  useEffect(() => {
    // Returning campaign user: /offers links to /app/login?campaign=promo30. If they're
    // already authenticated, hand them straight to the campaign checkout instead of
    // bouncing them to / (where the subscription gate would dump them on /onboarding).
    const params = new URLSearchParams(window.location.search);
    const campaignParam = params.get('campaign');
    const token = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('userId');

    if (campaignParam === 'promo30' && token && userId) {
      axios
        .post(
          `${process.env.REACT_APP_SERVER_URL}/campaign/checkout-session`,
          { userId },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((res) => {
          if (res.data?.checkoutUrl) {
            localStorage.removeItem('campaign');
            window.location.replace(res.data.checkoutUrl);
          } else {
            navigate('/');
          }
        })
        .catch((err) => {
          const status = err?.response?.status;
          const data = err?.response?.data;
          if (status === 409 && data?.alreadySubscribed) {
            localStorage.removeItem('campaign');
            message.info(data.message || 'You already have an active plan. Redirecting to your dashboard.', 5);
            navigate('/');
            return;
          }
          if (status && status >= 400) {
            localStorage.removeItem('campaign');
            console.error('[Campaign] Checkout failed (non-retryable, flag cleared):', err);
            navigate('/');
            return;
          }
          console.error('[Campaign] Transient error retrying campaign checkout:', err);
          navigate('/');
        });
      return;
    }

    const interval = setInterval(() => {
      const t = localStorage.getItem('accessToken');
      if (t) {
        navigate('/');
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Campaign flag TTL: 30 minutes. Prevents stale flags from forcing checkout
  // on unrelated future logins.
  const CAMPAIGN_TTL_MS = 30 * 60 * 1000;

  const readCampaign = (): string | null => {
    const raw = localStorage.getItem('campaign');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.value && parsed?.expiresAt && parsed.expiresAt > Date.now()) {
        return parsed.value;
      }
      localStorage.removeItem('campaign');
      return null;
    } catch {
      localStorage.removeItem('campaign');
      return null;
    }
  };

  // Detect campaign flag from URL and persist it with TTL. If user lands on /login
  // without ?campaign param, clear any stale flag from prior /offers visits.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const campaign = params.get('campaign');
    if (campaign) {
      localStorage.setItem(
        'campaign',
        JSON.stringify({ value: campaign, expiresAt: Date.now() + CAMPAIGN_TTL_MS })
      );
      console.log(`[Campaign] Detected campaign flag: ${campaign}`);
    } else {
      const stale = localStorage.getItem('campaign');
      if (stale) {
        localStorage.removeItem('campaign');
        console.log('[Campaign] Cleared stale campaign flag on naked /login visit');
      }
    }
  }, []);

  // Build the OAuth redirect URL, appending campaign param if present and not expired
  const buildOAuthUrl = (baseUrl: string): string => {
    const campaign = readCampaign();
    return campaign ? `${baseUrl}?campaign=${campaign}` : baseUrl;
  };

  return (
    <div className='auth-wrapper'>
      <div className='left'>
        <div className='content-wrapper'>
          <img
            className='logo'
            src={logo}
            alt='logo'
            onClick={() => {
              window.location.href = `${process.env.REACT_APP_URL}`;
            }}
            style={{ cursor: 'pointer' }}
          />
          <div className='top-section-details'>
            <h2>Hi 👋, let’s get familiar.</h2>
            <p>Let's create account and start writing blogs.</p>
          </div>
          <div className='google-login'>
            <button
              onClick={() => {
                window.location.href = buildOAuthUrl(`${process.env.REACT_APP_SERVER_URL}/google`);
              }}
            >
              <img src={googleImg} alt='google' />
              Continue with Google
            </button>
            <Divider>or</Divider>
            <button
              onClick={() => {
                window.location.href = buildOAuthUrl(`${process.env.REACT_APP_SERVER_URL}/auth/microsoft`);
              }}
            >
              <img src={microsofrtImg} height='15' alt='microsoft' />
              Continue with Microsoft
            </button>
          </div>
        </div>
      </div>
      <div className='right'>
        <div className='auth-blog-content'>
          <img className='blog-img' src={blogImg} alt='blog-img' />
          <h3>Conquer writer's block once and for all with Bloggr.ai.</h3>
          <p>
            Effortlessly create SEO-optimized, original content of any type in minutes with Bloggr.ai. Automate your
            content marketing to streamline your workflow and focus on growing your business.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
